import type { ActivationRoutingDiagnosis } from './types';

import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCompany } from '../queryKeys';

import { fetchActivationRoutingDiagnosis } from './fetchActivationRoutingDiagnosis';

/**
 * Generates the query options for the activation routing diagnosis query.
 *
 * @param companyId - The ID of the company (supplier) to diagnose.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getActivationRoutingDiagnosisQueryOptions = <
  TData = ActivationRoutingDiagnosis,
>(
  { companyId }: { companyId: UUID | undefined },
  options?: Omit<
    UseQueryOptions<ActivationRoutingDiagnosis, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryFn: companyId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchActivationRoutingDiagnosis(companyId, { signal })
      : skipToken,
    queryKey: queryKeysCompany.getActivationRoutingDiagnosis(companyId ?? ''),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the activation routing diagnosis
 * for a company (supplier) with a given companyId: why the supplier's
 * activation emails would go to the supplier directly or to Customer
 * Success, judged independently per document type. Requires ADMIN access.
 *
 * @param companyId - The ID of the company (supplier) to diagnose.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Companies/operation/get_activation_routing_diagnosis_vadmin_company__company_id__activation_routing_diagnosis_get
 */
export const useQueryActivationRoutingDiagnosis = <
  TData = ActivationRoutingDiagnosis,
>(
  companyId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<ActivationRoutingDiagnosis, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getActivationRoutingDiagnosisQueryOptions<TData>({ companyId }, options),
  );
